/**
 * Layout-independent keyboard resolution.
 *
 * Gameplay hotkeys must work on EVERY keyboard layout (Russian ЙЦУКЕН, AZERTY, …), not just US
 * QWERTY. The trap is `KeyboardEvent.key`, which is the *character the layout produces*: on a Russian
 * layout the physical W key reports `key === 'ц'`, A reports `'ф'`, S `'ы'`, D `'в'` — so a naive
 * `e.key === 'w'` check silently dies the moment the player switches layout.
 *
 * `KeyboardEvent.code` instead names the *physical key position* (`'KeyW'`, `'Digit1'`, `'ArrowUp'`),
 * which is identical across layouts. We canonicalize it back to the bare token the rest of the input
 * code already speaks (`'w'`, `'1'`, `'arrowup'`) so callers stay unchanged and WASD keeps meaning the
 * physical WASD cluster no matter what letters are painted on the keycaps.
 *
 * Falls back to `e.key` when `code` is empty/unknown (synthetic events, some IMEs, exotic devices) so
 * we never regress below the old behavior.
 */
export function physicalKey(e: KeyboardEvent): string {
  const code = e.code;
  // Letter keys: 'KeyW' → 'w'. Position is fixed; the painted glyph (w / ц / z on AZERTY) is irrelevant.
  if (code.length === 4 && code.startsWith('Key')) return code.charAt(3).toLowerCase();
  // Number row: 'Digit1' → '1'.
  if (code.length === 6 && code.startsWith('Digit')) return code.charAt(5);
  // Numpad digits: 'Numpad7' → '7' (only when NumLock yields a digit; otherwise key falls through).
  if (code.length === 7 && code.startsWith('Numpad')) {
    const d = code.charAt(6);
    if (d >= '0' && d <= '9') return d;
  }
  // Everything else (Arrow*, Escape, Enter, …) — `key` is already layout-independent for these.
  return e.key.toLowerCase();
}
